import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BraintreeContext } from "./BraintreeContext";
import braintree, {
  BraintreeError,
  type Client,
  type ThreeDSecure,
} from "braintree-web";
import { trackError } from "@/utils/error-monitoring";
import { SKIP_PAYMENT_GATEWAY } from "@/utils/get-service";
import type { ThreeDSecureVerificationData } from "braintree-web/three-d-secure";
import type { PaymentSession } from "@/payment/types";
import { PaymentDriverStatusContext } from "@/payment/PaymentDriverStatusContext";

export type BraintreeProviderProps = {
  session: PaymentSession | null;
  children: React.ReactNode;
};

export const BraintreeProvider = ({
  session,
  children,
}: BraintreeProviderProps) => {
  const [loading, setLoading] = useState(!SKIP_PAYMENT_GATEWAY);
  const [braintreeClient, setBraintreeClient] = useState<Client | null>(null);
  const [threeDSecure, setThreeDSecure] = useState<ThreeDSecure | null>(null);
  const [threeDSecureError, setThreeDSecureError] = useState<Error | null>(
    null,
  );
  const [initError, setInitError] = useState<Error | null>(null);

  const initializedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (SKIP_PAYMENT_GATEWAY) return;
    if (!session) return; // pending: nothing to initialise yet

    // React StrictMode mounts/unmounts/mounts in dev. Initializing twice would
    // create the Braintree client and 3D Secure (Cardinal Songbird) instance a
    // second time, which corrupts Cardinal's global state and makes verifyCard
    // hang. Guard so initialization runs once, when the session first arrives.
    if (initializedRef.current) return;
    initializedRef.current = true;

    const initializeBraintree = async () => {
      try {
        const client = await braintree.client.create({
          authorization: session.client_secret,
        });
        braintree.threeDSecure.create(
          {
            client,
            version: 2,
          },
          (createError?: BraintreeError, threeDSecure?: ThreeDSecure) => {
            if (createError) {
              trackError(createError as Error);
              if (mountedRef.current) {
                setThreeDSecureError(createError as Error);
                setLoading(false);
              }
              return;
            }

            if (!threeDSecure) {
              if (mountedRef.current) {
                setLoading(false);
              }
              return;
            }
            threeDSecure.on(
              "lookup-complete",
              (_data?: ThreeDSecureVerificationData, next?: () => void) => {
                next?.();
              },
            );

            if (mountedRef.current) {
              setThreeDSecure(threeDSecure);
              setLoading(false);
            }
          },
        );

        if (mountedRef.current) {
          setBraintreeClient(client);
        }
      } catch (error) {
        if (mountedRef.current) {
          setInitError(
            error instanceof Error ? error : new Error(String(error)),
          );
        }
      }
    };

    initializeBraintree();
    // The Braintree client + Cardinal 3DS are created once per provider mount and
    // intentionally NOT re-created when recreateSession() issues a new client_secret:
    // the existing client stays valid for the session's lifetime. The initializedRef
    // guard covers both StrictMode's dev double-mount and recreateSession re-runs.
  }, [session]);

  const regenerateBraintreeClient = useCallback(() => {
    console.warn(
      "regenerateClient is deprecated; use usePayment().recreateSession()",
    );
  }, []);

  const value = useMemo(
    () => ({
      isLoading: loading,
      braintreeClient,
      threeDSecure,
      threeDSecureError,
      regenerateClient: regenerateBraintreeClient,
    }),
    [
      loading,
      braintreeClient,
      threeDSecure,
      threeDSecureError,
      regenerateBraintreeClient,
    ],
  );

  const statusValue = useMemo(
    () => ({ error: threeDSecureError, isLoading: loading }),
    [threeDSecureError, loading],
  );

  if (initError) {
    throw initError;
  }

  return (
    <PaymentDriverStatusContext.Provider value={statusValue}>
      <BraintreeContext value={value}>{children}</BraintreeContext>
    </PaymentDriverStatusContext.Provider>
  );
};
